/**
 * FWA equivalent of NGD Browse `src/hooks/pdp/usePdpVoiceBotCallback.ts`.
 * Routing order: tool (`message.type`) → field command (`data.fields.type`) → default success.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as progressivePlansActionTypes from '../../../../pages/ProgressivePlans/@Redux/actionTypes';
import { useVoiceBotContext } from '../contexts/VoiceBotContext';
import { sendVoiceBotMessage } from '../helper';
import {
  buildAccessoryPageInformationText,
  buildExpressCheckoutCartPageInformationText,
  buildPerkPageInformationText,
  buildPlanPageInformationText,
  buildVoiceBotPageContextText,
  extractGetPageInformationCategory,
  findAvailablePerkByVoiceValue,
  findProgressivePlanByVoiceValue,
  getDisplayedPlansForVoiceBot,
  VOICE_BOT_FWA_ADDONS_PAGE,
  VOICE_BOT_FWA_EXPRESS_CHECKOUT_PAGE,
  VOICE_BOT_FWA_PLANS_PAGE,
  VOICE_BOT_FWA_STREAMLINE_PAGE,
} from '../planPageInformation';
import { finishVoiceBotWs, logVoiceBotWsInbound } from '../voiceBotSocketLogger';
import {
  VOICE_BOT_MESSAGE_TYPES,
  VOICE_BOT_MESSAGE_VALUES,
  VOICE_BOT_PAGE_INFO_CATEGORY,
  VOICE_BOT_TOOL_TYPES,
  applyFunctionCallActionFallbacks,
  parseVoiceBotSocketCommand,
} from '../voiceBotSocketConstants';

/**
 * FWA VoiceBot WebSocket bridge — mirrors NGD Browse `usePdpVoiceBotCallback`:
 * 1) Route by `message.type` (tool) first via `handleToolType`
 * 2) Then by `data.fields.type` (command) via `handleMessageType`
 * 3) Default `{ status: 'success' }`
 *
 * @param {string} pageName Voice context label (e.g. "FWA Plans Page")
 */
export function useFwaVoiceBotSocketCallback(pageName) {
  const dispatch = useDispatch();
  const { reportError, triggerAddToCart } = useVoiceBotContext();

  const [textMessage, setTextMessage] = useState('');

  const progressivePlans = useSelector((state) => state.progressivePlans);
  const selectedPlanInfo = progressivePlans?.selectedPlanInfo || {};
  const selectedPlan = selectedPlanInfo?.selectedPlan;
  const selectedPerks = selectedPlanInfo?.selectedPerks;
  const cartDetails = useSelector((state) => state.cartDetails);
  const addons = useSelector((state) => state.addons);
  const checkoutNew = useSelector((state) => state.checkoutNew);

  /**
   * Keep CES grounded on live Redux state (selected plan/perks, cart) via `textMessage`.
   * Previously gated on LQ + `pageInfoReceived`, so UI plan picks never updated the assistant.
   * `VoiceBotComponent` sends when `textMessage` changes and WebSocket is open.
   */
  useEffect(() => {
    const onPlansOrStreamline =
      pageName === VOICE_BOT_FWA_PLANS_PAGE || pageName === VOICE_BOT_FWA_STREAMLINE_PAGE;
    const onAddons = pageName === VOICE_BOT_FWA_ADDONS_PAGE;
    const onExpressCheckout = pageName === VOICE_BOT_FWA_EXPRESS_CHECKOUT_PAGE;

    if (!onPlansOrStreamline && !onAddons && !onExpressCheckout) return;

    if (onPlansOrStreamline) {
      const plansLoaded = progressivePlans?.getfwaplansLoaded;
      const hasLinePlans = !!progressivePlans?.progressivePlanAPiResponse?.data?.lineLevelPlans?.lines?.length;
      if (!plansLoaded || !hasLinePlans) return;
    }

    const next = buildVoiceBotPageContextText(
      pageName,
      progressivePlans,
      addons,
      cartDetails,
      checkoutNew
    );
    if (!next || !String(next).trim()) return;

    setTextMessage(next);
    sendVoiceBotMessage(next);
  }, [pageName, progressivePlans, addons, cartDetails, checkoutNew]);

  const runAddToCart = useCallback(() => {
    try {
      triggerAddToCart();
      return { status: 'success', message: 'Item added to cart' };
    } catch (error) {
      console.error('Add to cart failed:', error);
      reportError('Failed to add to cart');
      return { status: 'error', message: 'Failed to add to cart' };
    }
  }, [triggerAddToCart, reportError]);

  const withFallbackText = useCallback((text, fallbackText) => (text && String(text).trim() ? text : fallbackText), []);

  const handleGetPageInformationTool = useCallback(
    (message) => {
      const category = extractGetPageInformationCategory(message);
      const planText = () => buildPlanPageInformationText(progressivePlans, pageName);
      const perkText = () => buildPerkPageInformationText(progressivePlans, pageName);
      const accessoryText = () => buildAccessoryPageInformationText(addons, cartDetails, pageName);
      const cartText = () => buildExpressCheckoutCartPageInformationText(cartDetails, checkoutNew, pageName);
      const categoryConfig = {
        [VOICE_BOT_PAGE_INFO_CATEGORY.PLAN]: { getText: planText, emptyText: 'No plan details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.PLANS]: { getText: planText, emptyText: 'No plan details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.PERK]: { getText: perkText, emptyText: 'No perk details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.PERKS]: { getText: perkText, emptyText: 'No perk details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.ACCESSORY]: {
          getText: accessoryText,
          emptyText: 'No accessory details available',
        },
        [VOICE_BOT_PAGE_INFO_CATEGORY.ACCESSORIES]: {
          getText: accessoryText,
          emptyText: 'No accessory details available',
        },
        [VOICE_BOT_PAGE_INFO_CATEGORY.CART]: { getText: cartText, emptyText: 'No cart details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.CARTS]: { getText: cartText, emptyText: 'No cart details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.ITEM]: { getText: cartText, emptyText: 'No cart details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.ITEMS]: { getText: cartText, emptyText: 'No cart details available' },
        [VOICE_BOT_PAGE_INFO_CATEGORY.ORDER]: { getText: cartText, emptyText: 'No cart details available' },
      };
      const config = categoryConfig[category];
      if (!config) {
        /* Non-null string: ces-voice-assistant-client skips ws.send when response is falsy (null/undefined/""). */
        return (
          `[VoiceBot] get_page_information: unrecognized category "${category || '(empty)'}". ` +
          'Expected plan(s), perk(s), accessory(ies), or cart / order item(s).'
        );
      }
      return withFallbackText(config.getText(), config.emptyText);
    },
    [addons, cartDetails, checkoutNew, pageName, progressivePlans, withFallbackText]
  );

  const handleGetPlanDetailsTool = useCallback(() => {
    const planObj = selectedPlan && !Array.isArray(selectedPlan) ? selectedPlan : null;
    return {
      selectedPlan: planObj?.displayName || planObj?.name || 'No plan selected',
      selectedPerks: selectedPerks?.map((p) => p.perkTitle || p.name).filter(Boolean).join(', ') || 'No perks selected',
      monthlyPrice: planObj?.price?.discountedPrice ?? planObj?.price?.regularPrice ?? 0,
      cartItemCount: cartDetails?.cartDetailsJson?.lineInfo?.length || 0,
    };
  }, [selectedPlan, selectedPerks, cartDetails]);

  const handleNavigationTool = useCallback((navUrl) => {
    try {
      const parsedUrl = new URL(navUrl, window.location.origin);
      const isHttpProtocol = parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
      const isSameOrigin = parsedUrl.origin === window.location.origin;

      if (!isHttpProtocol || !isSameOrigin) {
        return { status: 'error', message: 'Blocked navigation to non-local URL', navigateUrl: navUrl };
      }

      const safePath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      window.location.href = safePath.startsWith('/') ? safePath : `/${safePath}`;
      return { status: 'success', navigateUrl: safePath };
    } catch (error) {
      return { status: 'error', message: 'Invalid navigation URL', navigateUrl: navUrl };
    }
  }, []);

  const handleToolType = useCallback(
    (toolType, message) => {
      if (toolType === VOICE_BOT_TOOL_TYPES.GET_PAGE_INFORMATION) {
        return handleGetPageInformationTool(message);
      }

      if (toolType === VOICE_BOT_TOOL_TYPES.GET_PLAN_DETAILS) {
        return handleGetPlanDetailsTool();
      }

      const clickOn = message?.data?.fields?.clickOn?.stringValue?.toLowerCase();
      if (clickOn === VOICE_BOT_MESSAGE_VALUES.ADD_TO_CART) {
        return runAddToCart();
      }

      const navUrl = message?.data?.fields?.url?.stringValue;
      const isNavigateTool =
        toolType === VOICE_BOT_MESSAGE_TYPES.NAVIGATE || toolType === VOICE_BOT_MESSAGE_TYPES.NAVIGATE_TO_PDP_SCREEN;
      if (navUrl && isNavigateTool) {
        return handleNavigationTool(navUrl);
      }

      return undefined;
    },
    [handleGetPageInformationTool, handleGetPlanDetailsTool, handleNavigationTool, runAddToCart]
  );

  const selectPerkByLabel = useCallback(
    (label) => {
      const found = findAvailablePerkByVoiceValue(progressivePlans, label);
      if (!found) {
        return {
          status: 'error',
          type: VOICE_BOT_MESSAGE_TYPES.SELECT_PERK,
          value: label,
          message: 'Perk not found or not available',
        };
      }
      const { perk: perkInfo, perkTitle } = found;
      dispatch({
        type: progressivePlansActionTypes.UPDATE_SELECT_DATA_PERK,
        payload: { perkInfo, perkTitle, toggleOn: true },
      });
      return {
        status: 'success',
        type: VOICE_BOT_MESSAGE_TYPES.SELECT_PERK,
        value: label,
        selectedPerk: {
          spoId: perkInfo?.spoId,
          perkTitle,
        },
      };
    },
    [dispatch, progressivePlans]
  );

  const handleSelectPlanMessage = useCallback(
    (messageValue) => {
      try {
        const matched = findProgressivePlanByVoiceValue(progressivePlans, messageValue);
        if (!matched) {
          const available = getDisplayedPlansForVoiceBot(progressivePlans)
            .map((p) => p?.displayName || p?.name)
            .filter(Boolean);
          return {
            status: 'error',
            type: VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN,
            value: messageValue,
            message: 'Plan not found for this address',
            availablePlanNames: available,
          };
        }
        dispatch({
          type: progressivePlansActionTypes.UPDATE_SELECT_DATA_PLAN,
          value: { selectedPlan: matched },
        });
        return {
          status: 'success',
          type: VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN,
          value: messageValue,
          selectedPlan: {
            displayName: matched.displayName || matched.name,
            planId: matched.planId,
            name: matched.name,
          },
        };
      } catch (error) {
        console.error('Plan selection failed:', error);
        return { status: 'error', type: VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN, value: messageValue };
      }
    },
    [dispatch, progressivePlans]
  );

  const handleSelectPerkMessage = useCallback(
    (messageType, messageValue, messageListValues) => {
      try {
        let labels = [];
        if (messageListValues?.length) {
          labels = [...messageListValues];
        } else if (messageValue) {
          labels = [messageValue];
        }
        if (!labels.length) {
          return {
            status: 'error',
            type: VOICE_BOT_MESSAGE_TYPES.SELECT_PERK,
            message: 'No perk name or list provided',
          };
        }
        if (labels.length === 1 && messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PERK) {
          return selectPerkByLabel(labels[0]);
        }
        const aggregated = [];
        for (const label of labels) {
          const one = selectPerkByLabel(label);
          aggregated.push(one);
          if (one.status === 'error') {
            return { ...one, partialResults: aggregated };
          }
        }
        return {
          status: 'success',
          type: VOICE_BOT_MESSAGE_TYPES.PERKS,
          selectedPerks: aggregated.map((r) => r.selectedPerk).filter(Boolean),
        };
      } catch (error) {
        console.error('Perk selection failed:', error);
        return { status: 'error', type: VOICE_BOT_MESSAGE_TYPES.SELECT_PERK, value: messageValue };
      }
    },
    [selectPerkByLabel]
  );

  const handleMessageType = useCallback(
    (messageType, messageValue, messageListValues) => {
      const isAddToCartMessage =
        messageValue?.toLowerCase() === VOICE_BOT_MESSAGE_VALUES.ADD_TO_CART ||
        messageType === VOICE_BOT_MESSAGE_TYPES.ADD_TO_CART;
      if (isAddToCartMessage) {
        return runAddToCart();
      }

      if (messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN || messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PLAN_ALIAS) {
        return handleSelectPlanMessage(messageValue);
      }

      if (messageType === VOICE_BOT_MESSAGE_TYPES.SELECT_PERK || messageType === VOICE_BOT_MESSAGE_TYPES.PERKS) {
        return handleSelectPerkMessage(messageType, messageValue, messageListValues);
      }

      return undefined;
    },
    [handleSelectPerkMessage, handleSelectPlanMessage, runAddToCart]
  );

  const cesWebSocketCallback = useCallback(
    (message) => {
      const parsed = parseVoiceBotSocketCommand(message);
      const { messageType, messageValue, messageListValues } = applyFunctionCallActionFallbacks(message, parsed);
      const wsType = typeof window !== 'undefined' ? window.wsType : '';
      const toolType = String(
        message?.type ??
          wsType ??
          ''
      ).toLowerCase();

      logVoiceBotWsInbound(message, toolType, { messageType, messageValue, messageListValues });

      const toolResult = handleToolType(toolType, message);
      if (toolResult !== undefined && toolResult !== null) {
        return finishVoiceBotWs(toolResult);
      }

      const messageResult = handleMessageType(messageType, messageValue, messageListValues);
      if (messageResult !== undefined) {
        return finishVoiceBotWs(messageResult);
      }

      return finishVoiceBotWs({ status: 'success' });
    },
    [handleToolType, handleMessageType]
  );

  return {
    cesWebSocketCallback,
    textMessage,
  };
}
